import React from 'react';
import { FormattedMessage } from 'react-intl';

import useNumberInput, { NumberInputError } from './number-input.controller';

const ID = 'number-input';
export interface NumberInputProps {
    prepend?: number;
    required?: boolean;
    valid?: boolean;
    isValidCallback?: (valid: boolean) => void;
    onChange?: (value: number) => void;
    onFocusChange?: () => void;
    step?: number;
    value?: string | number;
    errorMessage?: React.ReactNode;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    submitted?: boolean;
    min?: number;
    max?: number;
    id?: string;
    minLength?: number;
    maxLength?: number;
    isClearable?: boolean;
    isLoading?: boolean;
    className?: string;
    onBlur?: () => void;
}

export const NumberInput = (props: NumberInputProps) => {
    const viewProps = useNumberInput(props);

    return (
        <div
            className={'form-group ' + props.className}
            id={props.id || ID}>
            <span className='text-foreground-default text-sm font-medium'>
                {props.required ? <span className='text-status-danger me-1'>&#9679;</span> : <></>} {props.label}
                {props.helpMessage && (
                    <>
                        <br />
                        <small className='form-text text-muted'>{props.helpMessage}</small>
                    </>
                )}
            </span>
            <div className='inline-flex w-full items-start justify-start self-stretch'>
                {props.prepend !== undefined && (
                    <div className='bg-background-surface border-border-default flex h-11 items-center justify-start gap-2 overflow-hidden rounded-tl-md rounded-bl-md border-t border-b border-l px-4'>
                        <div className='text-foreground-default justify-start text-base font-medium'>{props.prepend}</div>
                    </div>
                )}
                <div
                    className={`bg-background-surface h-11 flex-1 ${props.prepend === undefined ? 'rounded-md' : 'rounded-tr-md rounded-br-md'} outline-border-default flex items-center justify-start gap-2 overflow-hidden outline-1 outline-offset-[-1px]`}>
                    <input
                        className={viewProps.inputClass + ' border-none text-base font-medium outline-none'}
                        autoFocus={props.autoFocus}
                        id={props.id}
                        value={props.value}
                        step={props.step || 1}
                        min={props.min}
                        max={props.max}
                        placeholder={props.placeholder}
                        minLength={props.minLength}
                        maxLength={props.maxLength}
                        type='number'
                        onFocus={() => {
                            viewProps.changeTouched(true);
                            props.onFocusChange && props.onFocusChange();
                        }}
                        required={props.required}
                        onChange={(event) => {
                            const numberValue = parseFloat(event.target.value);
                            if (isNaN(numberValue)) {
                                if (props.isClearable) {
                                    viewProps.changeTouched(false);
                                    props.isValidCallback && props.isValidCallback(true);
                                } else {
                                    props.isValidCallback && props.isValidCallback(false);
                                }
                            }
                            if (!isNaN(numberValue) || event.target.value === '') {
                                if (event.target.value.length > 0) {
                                    viewProps.changeTouched(true);
                                }
                                if (props.onChange) {
                                    props.onChange(numberValue);
                                    if (props.min !== undefined) {
                                        if (numberValue < props.min) {
                                            props.onChange(props.min);
                                        }
                                    }
                                    if (props.max !== undefined) {
                                        if (numberValue > props.max) {
                                            props.onChange(props.max);
                                        }
                                    }
                                    if (props.maxLength !== undefined) {
                                        if (numberValue.toString().length > props.maxLength) {
                                            const stringValue = numberValue.toString();
                                            props.onChange(parseFloat(stringValue.slice(0, props.maxLength)));
                                        }
                                    }
                                }
                            }
                            viewProps.isValid();
                        }}
                        disabled={props.disabled}
                        onBlur={props.onBlur}
                    />

                    {props.isLoading && (
                        <div
                            className='me-3'
                            role='status'>
                            <svg
                                aria-hidden='true'
                                className='fill-brand-primary text-brand-light size-4 animate-spin'
                                viewBox='0 0 100 101'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                    fill='currentColor'
                                />
                                <path
                                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                    fill='currentFill'
                                />
                            </svg>
                            <span className='sr-only'>Loading...</span>
                        </div>
                    )}
                </div>
            </div>

            {viewProps.valid === false && viewProps.touched === true ? (
                <>
                    {props.errorMessage === undefined ? (
                        <>
                            {viewProps.error === NumberInputError.MIN_LENGTH && MinLengthError(props.minLength)}
                            {viewProps.error === NumberInputError.MAX_LENGTH && MaxLengthError(props.maxLength)}
                            {viewProps.error === NumberInputError.MIN_MAX_LENGTH && MinMaxLengthError(props.minLength, props.maxLength)}
                        </>
                    ) : (
                        <span className='badge bg-gradient-danger mt-2'>
                            <small>{props.errorMessage}</small>
                        </span>
                    )}
                </>
            ) : (
                <></>
            )}
        </div>
    );
};

const MinLengthError = (minLength: number | undefined) => (
    <span className='badge bg-gradient-danger mt-2'>
        <small>
            <FormattedMessage
                id='NumberInput.MinLength'
                description='Min length error message'
                defaultMessage={`Minimum length is {minLength}`}
                values={{ minLength: minLength }}
            />
        </small>
    </span>
);

const MaxLengthError = (maxLength: number | undefined) => (
    <span className='badge bg-status-danger mt-2'>
        <small>
            <FormattedMessage
                id='NumberInput.maxLength'
                description='Max length error message'
                defaultMessage={`Maximum length is {maxLength}`}
                values={{ maxLength: maxLength }}
            />
        </small>
    </span>
);

const MinMaxLengthError = (minLength: number | undefined, maxLength: number | undefined) => (
    <span className='badge bg-gradient-danger mt-2'>
        <small>
            <FormattedMessage
                id='NumberInput.minMaxLength'
                description='Min max length error message'
                defaultMessage={`Length of the number should be between {minLength} and {maxLength}`}
                values={{ minLength: minLength, maxLength: maxLength }}
            />
        </small>
    </span>
);
